import {CampaignTreatmentSelect} from '@/components/CampaignTreatmentSelect';
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, Megaphone, Plus, Copy, Trash2, Send, Save, Users,
    ShieldCheck, Mail, MessageSquare, X, FileText, Sparkles,
    Sliders, ChevronDown, ChevronUp, Eye, Clock,
} from "lucide-react";
import { WhatsAppStudio } from "@/components/WhatsAppStudio";
import { staffApi } from "@/lib/staffApi";
import { Skeleton } from "@/components/BookingShell";

/**
 * Marketing — Composer, Templates and Campaign History (rewrite Feb-2026).
 *
 * Angela-first UX: no merge tags, no template variable syntax visible.
 * Personalisation is inserted via a button, audience is chosen from friendly
 * preset chips, numeric knobs live under "Advanced". Every send still goes
 * through the server-side consent audit trail.
 */
export function StaffMarketing() {
    const [tab, setTab] = useState("campaigns");
    return (
        <div className="space-y-5" data-testid="staff-marketing">
            <div>
                <Link to="/staff" className="text-[10px] uppercase tracking-[0.18em] text-white/50 hover:text-gold inline-flex items-center gap-1">
                    <ArrowLeft className="h-3 w-3" /> Back
                </Link>
                <h1 className="mt-2 font-serif font-light text-3xl md:text-4xl text-white flex items-center gap-3">
                    <Megaphone className="h-6 w-6 text-gold" /> Marketing
                </h1>
                <p className="mt-2 text-sm text-white/60 max-w-2xl">
                    Reach the right customers with beautiful campaigns. Choose your audience, write your message, hit send.
                </p>
            </div>

            <div className="flex gap-2 border-b border-white/10">
                {[
                    { id: "campaigns", label: "Campaigns" },
                    { id: "whatsapp", label: "Create WhatsApp / approvals" },
                ].map((t) => (
                    <button key={t.id} onClick={() => setTab(t.id)}
                            data-testid={`mkt-tab-${t.id}`}
                            className={`h-10 px-4 text-sm font-medium transition-colors ${
                                tab === t.id
                                    ? "text-gold border-b-2 border-gold"
                                    : "text-white/60 hover:text-white"
                            }`}>
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === "campaigns" && <CampaignsTab />}
            {tab === "whatsapp" && <WhatsAppStudio />}
        </div>
    );
}

/* ---------------------------------------------------------------------------
 * Campaigns list
 * ------------------------------------------------------------------------- */
function CampaignsTab() {
    const [library,setLibrary]=useState([]);
    const [libraryError,setLibraryError]=useState('');
    useEffect(()=>{let active=true;const refresh=()=>staffApi.marketingLibrary().then(r=>{if(active){setLibrary(r.templates||[]);setLibraryError('');}}).catch(e=>{if(active)setLibraryError(e.message||'Could not check WhatsApp approvals');});refresh();const timer=setInterval(refresh,30000);return()=>{active=false;clearInterval(timer);};},[]);
    const [list, setList] = useState(null);
    const [editing, setEditing] = useState(null); // null | { new: true } | campaign
    const load = () => staffApi.listMarketingCampaigns().then((r) => setList(r.campaigns || [])).catch(() => setList([]));
    useEffect(() => { load(); }, []);

    if (!list) return <Skeleton className="h-40" />;

    const active = list.filter((c) => c.status !== "sent");
    const past = list.filter((c) => c.status === "sent");

    return (
        <div className="space-y-4">
            <section className="space-y-3"><h2 className="font-serif text-2xl text-white">Campaign library</h2>
                {libraryError && <p role="alert" className="text-amber-200">{libraryError}. Email campaigns are still available.</p>}
                <div className="grid gap-3 sm:grid-cols-2">{library.filter(t=>!t.campaign_archived && (!t.whatsapp_authoring || t.whatsapp_available || t.campaign_builtin)).map(t=><div key={t.id} className="surface-highlight rounded-xl p-4 space-y-2"><h3 className="text-white font-semibold">{t.name}</h3><p className="text-sm text-white/70">{t.whatsapp_available?'Email and WhatsApp ready':(t.approval_status==='PENDING'?'Email ready — WhatsApp awaiting Meta approval':t.approval_status==='UNVERIFIED'?'Email ready — WhatsApp status unavailable':'Email ready — check WhatsApp approval status')}</p><button type="button" className="rounded-full bg-gold text-navy px-4 py-2" onClick={()=>setEditing({...t,id:undefined,template_id:t.id,status:'draft',channel:'email'})}>Use campaign</button></div>)}</div>
            </section>
            <h2 className="font-serif text-2xl text-white">Drafts, scheduled and sent</h2>
            <div className="flex justify-between items-center">
                <div className="text-sm text-white/60">{list.length} campaign{list.length === 1 ? "" : "s"}</div>
                <button onClick={() => setEditing({ new: true })} data-testid="mkt-new-campaign"
                        className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-gold text-navy font-semibold text-sm">
                    <Plus className="h-4 w-4" /> New email campaign
                </button>
            </div>
            {active.length > 0 && (
                <section className="space-y-2">
                    <h3 className="text-[10px] uppercase tracking-[0.22em] text-gold/80 font-semibold">Drafts & scheduled</h3>
                    {active.map((c) => <CampaignRow key={c.id} camp={c} onOpen={setEditing} onChanged={load} />)}
                </section>
            )}
            {past.length > 0 && (
                <section className="space-y-2 pt-2">
                    <h3 className="text-[10px] uppercase tracking-[0.22em] text-gold/80 font-semibold">Sent</h3>
                    {past.map((c) => <CampaignRow key={c.id} camp={c} onOpen={setEditing} onChanged={load} />)}
                </section>
            )}
            {list.length === 0 && (
                <div className="rounded-2xl surface-highlight p-8 text-center text-white/70">
                    No campaigns yet. Tap <span className="text-gold font-semibold">New campaign</span> to reach opted-in customers.
                </div>
            )}
            {editing && (
                <CampaignEditor
                    initial={editing.new ? null : editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }}
                />
            )}
        </div>
    );
}

function CampaignRow({ camp: c, onOpen, onChanged }) {
    const doDuplicate = async () => {
        try { await staffApi.duplicateMarketingCampaign(c.id); toast.success("Duplicated"); onChanged(); }
        catch (e) { toast.error(e?.message || "Duplicate failed"); }
    };
    const doDelete = async () => {
        if (!window.confirm(`Delete draft "${c.name}"?`)) return;
        try { await staffApi.deleteMarketingCampaign(c.id); toast.success("Deleted"); onChanged(); }
        catch (e) { toast.error(e?.message || "Delete failed"); }
    };
    const badge = {
        draft: "border-slate-400/50 text-slate-200",
        scheduled: "border-amber-400/50 text-amber-200",
        sending: "border-blue-400/50 text-blue-200",
        sent: "border-emerald-500/50 text-emerald-200",
        failed: "border-red-400/50 text-red-200",
    }[c.status] || "border-white/20 text-white/70";
    return (
        <div className="rounded-xl surface-highlight shadow-md p-4 flex items-center gap-3"
             data-testid={`mkt-camp-${c.id}`}>
            <button onClick={() => onOpen(c)} className="flex-1 min-w-0 text-left"
                    data-testid={`mkt-camp-open-${c.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-white font-semibold text-[15px] truncate">{c.name}</div>
                    <span className={`text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${badge}`}>
                        {c.status}
                    </span>
                </div>
                <div className="text-[11px] text-white/60 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                    <span>{c.channel === "email" ? <Mail className="inline h-3 w-3 mr-0.5" /> : <MessageSquare className="inline h-3 w-3 mr-0.5" />}{c.channel}</span>
                    {c.recipient_count > 0 && <><span>·</span><span>{c.recipient_count} recipients</span></>}
                    {c.delivered > 0 && <><span>·</span><span className="text-emerald-300">{c.delivered} delivered</span></>}
                    {c.failed > 0 && <><span>·</span><span className="text-red-300">{c.failed} failed</span></>}
                    {c.sent_at && <><span>·</span><span>{new Date(c.sent_at).toLocaleDateString()}</span></>}
                </div>
            </button>
            <div className="flex gap-1 shrink-0">
                <button onClick={doDuplicate} title="Duplicate" data-testid={`mkt-camp-dup-${c.id}`}
                        className="p-2 text-white/70 hover:text-gold rounded-full">
                    <Copy className="h-4 w-4" />
                </button>
                {c.status === "draft" && (
                    <button onClick={doDelete} title="Delete" data-testid={`mkt-camp-delete-${c.id}`}
                            className="p-2 text-white/70 hover:text-red-300 rounded-full">
                        <Trash2 className="h-4 w-4" />
                    </button>
                )}
            </div>
        </div>
    );
}

/* ---------------------------------------------------------------------------
 * Personalisation dictionary — friendly label -> stored merge tag.
 * Only these six tags are actually substituted by the backend today; the rest
 * are treated as prompts to Angela and left as-is inside the message body
 * (she can hand-fill numbers before sending).
 * ------------------------------------------------------------------------- */
const PERSONALISATIONS = [
    { label: "First name",        tag: "{{first_name}}" },
    { label: "Treatment",         tag: "{{treatment}}" },
    { label: "Booking link",      tag: "{{booking_link}}" },
    { label: "Discount",          tag: "{{discount}}" },
    { label: "Clinic name",       tag: "{{clinic_name}}" },
    { label: "Practitioner name", tag: "{{practitioner_name}}" },
    { label: "Appointment date",  tag: "{{appointment_date}}" },
    { label: "Amount owed",       tag: "{{amount_owed}}" },
    { label: "Remaining balance", tag: "{{remaining_balance}}" },
];

/* ---------------------------------------------------------------------------
 * Audience presets — friendly labels mapped to backend `filters`.
 * ------------------------------------------------------------------------- */
const AUDIENCE_PRESETS = [
    { id: "all",         label: "All customers",                          filters: {} },
    { id: "new",         label: "New customers",                          filters: { new_customers_only: true } },
    { id: "existing",    label: "Existing customers",                     filters: { returning_customers_only: true } },
    { id: "future",      label: "Customers with future appointments",     filters: { has_future_appointment: true } },
    { id: "no_future",   label: "Customers with no future bookings",      filters: { has_future_appointment: false } },
    {
        id: "birthday",
        label: "Birthday this month",
        get filters() { return { birthday_month: new Date().getMonth() + 1 }; },
    },
    { id: "due_review",  label: "Customers due a review",                 filters: { last_appointment_within_days: 14 } },
    { id: "due_rebook",  label: "Customers due to rebook",                filters: { days_since_last_appointment_min: 84, has_future_appointment: false } },
    { id: "unpaid",      label: "Customers with unpaid balances",         filters: { has_unpaid_balance: true } },
];

/* ---------------------------------------------------------------------------
 * Sample values used for Live Preview & Send Test — swap merge tags with
 * realistic content so Angela sees a lifelike preview.
 * ------------------------------------------------------------------------- */
const SAMPLE_VALUES = {
    first_name: "Emma",
    last_name: "",
    treatment: "Anti-Wrinkle Consultation",
    booking_link: "https://poutaesthetics.co.uk/book",
    discount: "15",
    clinic_name: "Pout Aesthetics",
    practitioner_name: "Angela",
    appointment_date: "Tuesday 24 February · 10:30",
    amount_owed: "£45.00",
    remaining_balance: "£45.00",
};

function renderWithSamples(text) {
    if (!text) return "";
    return String(text).replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => (
        SAMPLE_VALUES[k] !== undefined ? SAMPLE_VALUES[k] : `[${k}]`
    ));
}

/* ---------------------------------------------------------------------------
 * Campaign editor — guided single-modal flow
 * ------------------------------------------------------------------------- */
function CampaignEditor({ initial, onClose, onSaved }) {
    const [form, setForm] = useState(initial || {
        name: "", channel: "email", subject: "", body_html: "", body_text: "", sms_body: "",
        template_id: null, filters: {},
    });
    const [savedId,setSavedId]=useState(initial?.id || null);
    const [audience, setAudience] = useState("all");
    const [templates, setTemplates] = useState([]);
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [confirmSend, setConfirmSend] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [showInsert, setShowInsert] = useState(false);
    const [showLivePreview, setShowLivePreview] = useState(true);
    const [testOpen, setTestOpen] = useState(false);
    const [testTo, setTestTo] = useState("");
    const [scheduleOpen, setScheduleOpen] = useState(false);
    const [scheduleAt, setScheduleAt] = useState("");
    const bodyRef = useRef(null);
    const subjectRef = useRef(null);
    const [focused, setFocused] = useState("body"); // "body" | "subject"
    const whatsappUnmapped = form.channel !== "email" && !templates.find(t=>t.id===form.template_id)?.whatsapp_available;
    const isDraft = !initial || initial.status === "draft";
    const readOnly = initial && !isDraft;

    useEffect(() => { staffApi.marketingLibrary().then((r) => setTemplates(r.templates || [])).catch(() => {}); }, []);

    const setF = (patch) => {setForm((f) => ({ ...f, ...patch }));setPreview(null);};
    const applyTemplate = (tid) => {
        const t = templates.find((x) => x.id === tid);
        if (!t) { setF({ template_id: null }); return; }
        setF({
            template_id: tid, subject: t.subject || "", body_html: t.body_html || "",
            body_text: t.body_text || "", sms_body: t.sms_body || "", meta_template_name:t.meta_template_name, meta_variables:t.meta_variables,
        });
    };

    const applyAudience = (id) => {
        setAudience(id);
        const preset = AUDIENCE_PRESETS.find((p) => p.id === id);
        setF({ filters: preset ? { ...preset.filters } : {} });
        setPreview(null); // audience changed → recipient preview stale
    };

    /* Insert a merge tag at the caret of the currently-focused field. */
    const insertTag = (tag) => {
        const ref = focused === "subject" ? subjectRef.current : bodyRef.current;
        if (!ref) return;
        const start = ref.selectionStart ?? ref.value.length;
        const end = ref.selectionEnd ?? ref.value.length;
        const before = ref.value.slice(0, start);
        const after = ref.value.slice(end);
        const newValue = `${before}${tag}${after}`;
        if (focused === "subject") setF({ subject: newValue });
        else if (form.channel !== "whatsapp") setF({ body_html: newValue });
        else setF({ sms_body: newValue });
        // Restore focus + move caret after the inserted tag on next tick
        setTimeout(() => {
            try {
                ref.focus();
                const pos = start + tag.length;
                ref.setSelectionRange(pos, pos);
            } catch (_) { /* older browsers */ }
        }, 0);
        setShowInsert(false);
    };

    const save = async () => {
        setBusy(true);
        try {
            let campaign;
            if (savedId) {
                const r = await staffApi.patchMarketingCampaign(savedId, form);
                campaign = r.campaign;
            } else {
                const r = await staffApi.createMarketingCampaign(form);
                campaign = r.campaign;
            }
            setSavedId(campaign.id);
            toast.success("Draft saved");
            return campaign;
        } catch (e) { toast.error(e?.message || "Save failed"); return null; }
        finally { setBusy(false); }
    };

    const previewNow = async () => {
        const c = await save();
        if (!c) return;
        try {
            const r = await staffApi.previewCampaignRecipients(c.id);
            setPreview({ ...r, campaignId: c.id });
        } catch (e) { toast.error(e?.message || "Preview failed"); }
    };

    const doSend = async () => {
        const c = await save();
        if (!c) return;
        setBusy(true);
        try {
            await staffApi.sendMarketingCampaign(c.id);
            toast.success("Campaign queued for sending");
            setConfirmSend(false);
            onSaved();
        } catch (e) { toast.error(e?.message || "Send failed"); }
        finally { setBusy(false); }
    };

    const sendTest = async () => {
        if (!testTo.trim()) { toast.error("Enter an address to send the test to"); return; }
        setBusy(true);
        try {
            const bodyText = form.channel !== "whatsapp" ? form.body_html : form.sms_body;
            const r = await staffApi.testSendComms({
                channel: form.channel,
                to: testTo.trim(),
                subject: form.subject || "",
                body: bodyText || "",
            });
            if (r.status === "sent") toast.success(`Test sent to ${r.to}`);
            else if (r.status === "not_configured") toast.error(r.message || "This channel isn't enabled yet");
            else toast.error(r.message || r.error || "Test send failed");
            setTestOpen(false);
        } catch (e) { toast.error(e?.message || "Test send failed"); }
        finally { setBusy(false); }
    };

    const scheduleSend = async () => {
        if (!scheduleAt) { toast.error("Pick a date and time"); return; }
        // The datetime-local input returns a naive local time; convert to UTC ISO.
        const local = new Date(scheduleAt);
        if (Number.isNaN(local.getTime())) { toast.error("Invalid date/time"); return; }
        if (local.getTime() <= Date.now()) { toast.error("Choose a time in the future"); return; }
        const c = await save();
        if (!c) return;
        setBusy(true);
        try {
            await staffApi.scheduleMarketingCampaign(c.id, local.toISOString());
            toast.success(`Scheduled for ${local.toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}`);
            setScheduleOpen(false);
            onSaved();
        } catch (e) { toast.error(e?.message || "Schedule failed"); }
        finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-stretch sm:items-center justify-center"
             onClick={onClose} data-testid="mkt-editor">
            <div onClick={(e) => e.stopPropagation()}
                 className="w-full sm:max-w-2xl m-0 sm:m-4 rounded-none sm:rounded-2xl glass glass-strong border hairline flex flex-col max-h-full sm:max-h-[92vh] overflow-hidden">
                <header className="p-4 sm:p-5 border-b border-white/10 flex items-center justify-between">
                    <h2 className="font-serif text-white text-xl">
                        {initial?.id ? initial.name : "New campaign"}
                    </h2>
                    <button onClick={onClose} data-testid="mkt-editor-close" className="text-white/60 hover:text-white p-1"><X className="h-5 w-5" /></button>
                </header>
                <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-5 space-y-5">
                    {/* ------- Step 1 · Campaign type ------- */}
                    <Step number={1} title="Campaign type">
                        <Field label="Name this campaign">
                            <input value={form.name} onChange={(e) => setF({ name: e.target.value })}
                                   disabled={readOnly}
                                   data-testid="mkt-name"
                                   placeholder="e.g. October promo"
                                   className="w-full h-11 bg-transparent border hairline rounded-md px-3 text-white text-sm" />
                        </Field>
                        <Field label="How should it be sent?">
                            <div className="flex flex-wrap gap-2">
                                {[
                                    { v: "email", label: "Email", icon: Mail },
                                    { v: "both", label: "Both", icon: Mail },
                                    { v: "whatsapp", label: "WhatsApp", icon: MessageSquare },
                                ].map((c) => (
                                    <button key={c.v} type="button" disabled={readOnly || (c.v !== "email" && !templates.find(t=>t.id===form.template_id)?.whatsapp_available)}
                                            onClick={() => setF({ channel: c.v })}
                                            data-testid={`mkt-channel-${c.v}`}
                                            className={`h-10 px-4 rounded-full border text-xs uppercase tracking-[0.14em] inline-flex items-center gap-1.5 ${form.channel === c.v ? "bg-gold text-navy border-gold font-semibold" : "hairline text-white/75 hover:border-gold/60"}`}>
                                        <c.icon className="h-3.5 w-3.5" /> {c.label}
                                    </button>
                                ))}
                            </div>
                        </Field>
                    </Step>

                    {form.channel !== "email" && <div role="status" className="rounded-xl border border-gold/40 p-3 text-sm text-white/80">WhatsApp campaigns use a Meta-approved marketing template. The message sent is the approved template, with its personalisation fields—not freely edited text here. {whatsappUnmapped && "This campaign has no linked Meta template yet. You can save a draft, but sending and scheduling are unavailable until it is connected."}</div>}
                    {form.channel === 'both' && <p className="text-sm text-gold">Both sends two messages to customers who have opted in to both channels. Customers opted in to only one receive that channel only. Recipient totals count messages.</p>}
                    {form.channel !== 'email' && <div className="rounded-xl bg-white/10 p-4 space-y-3"><h3 className="text-white font-semibold">Approved WhatsApp message</h3><p className="text-white/80 whitespace-pre-wrap">{templates.find(t=>t.id===form.template_id)?.whatsapp_body?.replace(/\[(Treatment name|Offer amount)\]/g,(match,label)=>form.personalisation?.[label==='Treatment name'?'treatment':'discount']||match)}</p>{['discount','treatment'].filter(k=>(templates.find(t=>t.id===form.template_id)?.meta_variables||[]).includes(k)).map(k=><label key={k} className="block text-sm text-white">{k==='discount'?'Offer amount (for example 10%)':'Treatment name'}{k==='treatment'?<CampaignTreatmentSelect disabled={readOnly} value={form.personalisation?.treatment||''} onChange={value=>setF({personalisation:{...form.personalisation,treatment:value}})}/>:<input className="block w-full mt-1 rounded-lg p-3 bg-white text-navy" disabled={readOnly} value={form.personalisation?.[k]||''} onChange={e=>setF({personalisation:{...form.personalisation,[k]:e.target.value}})}/>}</label>)}</div>}
                    {form.channel === 'email' && ['discount','treatment'].filter(k=>((form.subject||'')+(form.body_html||'')+(form.body_text||'')).includes('{{'+k+'}}')).map(k=><label key={k} className="block text-white">{k==='discount'?'Offer amount (include % or £, for example 10%)':'Treatment name'}{k==='treatment'?<CampaignTreatmentSelect disabled={readOnly} value={form.personalisation?.treatment||''} onChange={value=>setF({personalisation:{...form.personalisation,treatment:value}})}/>:<input className="block w-full rounded-lg bg-white text-navy p-3" value={form.personalisation?.[k]||''} onChange={e=>setF({personalisation:{...form.personalisation,[k]:e.target.value}})}/>}</label>)}
                    {/* ------- Step 2 · Audience ------- */}
                    <Step number={2} title="Choose your audience">
                        <label className="block text-sm text-white/80">Audience type
                            <select value={audience} disabled={readOnly} onChange={e => applyAudience(e.target.value)} data-testid="mkt-audience-select" className="mt-2 w-full min-w-0 h-11 rounded-lg bg-white text-navy px-3">
                                {AUDIENCE_PRESETS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                            </select>
                        </label>
                        <button type="button" onClick={() => setShowAdvanced((s) => !s)}
                                data-testid="mkt-toggle-advanced"
                                className="text-[11px] uppercase tracking-[0.14em] text-white/55 hover:text-gold inline-flex items-center gap-1 mt-1">
                            <Sliders className="h-3 w-3" />
                            {showAdvanced ? "Hide advanced filters" : "Show advanced filters"}
                            {showAdvanced ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                        </button>
                        {showAdvanced && (
                            <AdvancedFilters filters={form.filters || {}}
                                             onChange={(filters) => { setF({ filters }); setAudience("all"); setPreview(null); }}
                                             disabled={readOnly} />
                        )}
                    </Step>

                    {/* ------- Step 3 · Template ------- */}
                    {!initial?.template_id && templates.length > 0 && (
                        <Step number={3} title="Start from a template (optional)">
                            <Field label="Template">
                                <select value={form.template_id || ""} onChange={(e) => applyTemplate(e.target.value)}
                                        disabled={readOnly}
                                        data-testid="mkt-template"
                                        className="w-full h-11 bg-navy border hairline rounded-md px-3 text-white text-sm">
                                    <option value="">Start from scratch</option>
                                    {templates.filter(t=>!t.campaign_archived && (form.channel === "email" || t.whatsapp_available)).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                                </select>
                            </Field>
                        </Step>
                    )}

                    {/* ------- Step 4 · Message ------- */}
                    {form.channel !== "whatsapp" && <Step number={!initial?.template_id && templates.length > 0 ? 4 : 3} title="Write your email">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                            <div className="text-[11px] text-white/50">
                                Add details such as a first name. Each customer receives their own details; the preview uses examples.
                            </div>
                            <div className="relative">
                                <button type="button" onClick={() => setShowInsert((s) => !s)} disabled={readOnly}
                                        data-testid="mkt-insert-personalisation"
                                        className="h-9 px-3 rounded-full border border-gold/50 text-gold text-xs font-semibold inline-flex items-center gap-1 hover:bg-gold/10">
                                    <Sparkles className="h-3 w-3" /> Insert personalisation
                                </button>
                                {showInsert && (
                                    <div className="absolute right-0 mt-2 w-64 z-10 rounded-xl glass glass-strong border hairline p-1 shadow-2xl"
                                         data-testid="mkt-insert-menu">
                                        {PERSONALISATIONS.map((p) => (
                                            <button key={p.tag} type="button"
                                                    onClick={() => insertTag(p.tag)}
                                                    data-testid={`mkt-insert-${p.tag.replace(/[^a-z_]/g, "")}`}
                                                    className="w-full text-left px-3 py-2 rounded-lg text-sm text-white/85 hover:bg-gold/10 hover:text-gold">
                                                {p.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        {form.channel !== "whatsapp" && (
                            <Field label="Subject">
                                <input ref={subjectRef}
                                       value={form.subject || ""}
                                       onChange={(e) => setF({ subject: e.target.value })}
                                       onFocus={() => setFocused("subject")}
                                       disabled={readOnly}
                                       data-testid="mkt-subject"
                                       placeholder="Give your email a subject line"
                                       className="w-full h-11 bg-transparent border hairline rounded-md px-3 text-white text-sm" />
                            </Field>
                        )}
                        <Field label={form.channel !== "whatsapp" ? "Message" : `${form.channel.toUpperCase()} message`}>
                            <textarea
                                ref={bodyRef}
                                value={form.channel !== "whatsapp" ? (form.body_html || "") : (form.sms_body || "")}
                                onChange={(e) => setF(form.channel !== "whatsapp"
                                    ? { body_html: e.target.value }
                                    : { sms_body: e.target.value })}
                                onFocus={() => setFocused("body")}
                                disabled={readOnly}
                                data-testid="mkt-body"
                                rows={8}
                                placeholder={`Hi there,\n\nTap "Insert personalisation" to add the customer's name, treatment, or a booking link.`}
                                className="w-full bg-transparent border hairline rounded-md p-3 text-white text-sm leading-relaxed"
                            />
                        </Field>
                    </Step>}

                    {/* ------- Live preview — always reflects the current draft ------- */}
                    <div className="rounded-2xl border hairline bg-white/[0.02] overflow-hidden" data-testid="mkt-live-preview-panel">
                        <button type="button" onClick={() => setShowLivePreview((s) => !s)}
                                data-testid="mkt-toggle-live-preview"
                                className="w-full flex items-center justify-between px-4 py-3">
                            <span className="text-sm text-white/85 font-semibold inline-flex items-center gap-2">
                                <Eye className="h-4 w-4 text-gold" /> Live preview
                            </span>
                            <span className="text-[10px] uppercase tracking-[0.14em] text-white/50 inline-flex items-center gap-1">
                                {showLivePreview ? "Hide" : "Show"}
                                {showLivePreview ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                            </span>
                        </button>
                        {showLivePreview && (
                            <div className="px-4 pb-4">
                                {form.channel !== "whatsapp" ? (
                                    <EmailPreview subject={form.subject || ""} body={form.body_html || ""} />
                                ) : (
                                    <p className="whitespace-pre-wrap text-white">{templates.find(t=>t.id===form.template_id)?.whatsapp_body?.replace(/\[(Treatment name|Offer amount)\]/g,(match,label)=>form.personalisation?.[label==='Treatment name'?'treatment':'discount']||match)}</p>
                                )}
                                <div className="mt-2 text-[10px] text-white/40 italic">
                                    Preview uses sample values (e.g. name "Emma", 15% discount). Real customer values are filled in when the campaign sends.
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ------- Step 5 · Preview recipients ------- */}
                    {preview && (
                        <div className="rounded-xl surface-highlight p-4 space-y-1" data-testid="mkt-preview">
                            <div className="flex items-center gap-2 text-emerald-300 text-xs uppercase tracking-[0.16em] font-semibold">
                                <Users className="h-4 w-4" /> Recipient preview
                            </div>
                            <div className="text-sm text-white/90">
                                <span className="text-gold font-semibold" data-testid="mkt-preview-deliverable">{preview.deliverable ?? preview.eligible}</span> deliverable
                                <span className="ml-2 text-white/50 text-xs">({preview.eligible} consent-eligible)</span>
                            </div>
                            <div className="text-[11px] text-white/55 mt-0.5 space-y-0.5">
                                {(preview.missing_address ?? 0) > 0 && (
                                    <div data-testid="mkt-preview-missing">· <span className="text-amber-200/85">{preview.missing_address}</span> consent-eligible but no address to send to</div>
                                )}
                                {(preview.invalid_address ?? 0) > 0 && (
                                    <div>· <span className="text-amber-200/85">{preview.invalid_address}</span> consent-eligible but address is invalid</div>
                                )}
                                {(preview.excluded_opt_out ?? 0) > 0 && (
                                    <div>· {preview.excluded_opt_out} opted-out (excluded)</div>
                                )}
                            </div>
                            {preview.sample?.length > 0 && (
                                <div className="text-xs text-white/60 pt-1">
                                    Example: {preview.sample.slice(0, 3).map((s) => s.name).join(", ")}
                                    {preview.sample.length > 3 ? "…" : ""}
                                </div>
                            )}
                        </div>
                    )}
                {/* ------- Step 6 · Send ------- */}
                <footer className="p-4 sm:p-5 border-t border-white/10 flex flex-wrap gap-2 justify-between">
                    {!readOnly ? (
                        <>
                            <button onClick={onClose} className="h-10 px-4 rounded-full border hairline text-white/80 text-sm">
                                Cancel
                            </button>
                            <div className="flex gap-2 flex-wrap">
                                <button onClick={save} disabled={busy || !form.name}
                                        data-testid="mkt-save-draft"
                                        className="h-10 px-4 rounded-full border border-gold/50 text-gold text-sm font-semibold disabled:opacity-40 inline-flex items-center gap-1">
                                    <Save className="h-4 w-4" /> Save draft
                                </button>
                                <button onClick={() => setTestOpen(true)} disabled={busy || !form.name || form.channel !== "email"}
                                        data-testid="mkt-send-test-btn"
                                        className="h-10 px-4 rounded-full border border-white/20 text-white/85 text-sm font-medium disabled:opacity-40 inline-flex items-center gap-1">
                                    <Send className="h-4 w-4" /> Send test
                                </button>
                                <button onClick={previewNow} disabled={busy || !form.name}
                                        data-testid="mkt-preview-btn"
                                        className="h-10 px-4 rounded-full border border-white/20 text-white/85 text-sm font-medium disabled:opacity-40 inline-flex items-center gap-1">
                                    <Users className="h-4 w-4" /> Recipients
                                </button>
                                <button onClick={() => setScheduleOpen(true)} disabled={busy || !form.name || whatsappUnmapped}
                                        data-testid="mkt-schedule-btn"
                                        className="h-10 px-4 rounded-full border border-white/20 text-white/85 text-sm font-medium disabled:opacity-40 inline-flex items-center gap-1">
                                    <Clock className="h-4 w-4" /> Schedule
                                </button>
                                <button onClick={() => setConfirmSend(true)}
                                        disabled={busy || !form.name || !preview || whatsappUnmapped}
                                        data-testid="mkt-send-btn"
                                        className="h-10 px-5 rounded-full bg-gold text-navy font-semibold text-sm disabled:opacity-40 inline-flex items-center gap-1">
                                    <Send className="h-4 w-4" /> Send now
                                </button>
                            </div>
                        </>
                    ) : (
                        <button onClick={onClose} className="ml-auto h-10 px-5 rounded-full bg-gold text-navy font-semibold text-sm">
                            Close
                        </button>
                    )}
                </footer>
                </div>
            </div>

            {testOpen && (
                <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center"
                     onClick={() => !busy && setTestOpen(false)}
                     data-testid="mkt-test-dialog">
                    <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md m-4 rounded-2xl glass glass-strong border hairline p-6 space-y-3">
                        <div className="flex items-center gap-2 text-gold">
                            <Send className="h-5 w-5" />
                            <h3 className="font-serif text-white text-xl">Send test to yourself</h3>
                        </div>
                        <p className="text-sm text-white/70">
                            {form.channel !== "whatsapp"
                                ? "We'll send this exact email to the address below, with sample personalisation. Only you receive it."
                                : `${form.channel.toUpperCase()} test send is not available yet (provider not configured).`}
                        </p>
                        <Field label={form.channel !== "whatsapp" ? "Email address" : "Phone number"}>
                            <input value={testTo} onChange={(e) => setTestTo(e.target.value)}
                                   data-testid="mkt-test-to"
                                   placeholder={form.channel !== "whatsapp" ? "you@yourclinic.co.uk" : "+44 7…"}
                                   className="w-full h-11 bg-transparent border hairline rounded-md px-3 text-white text-sm" />
                        </Field>
                        <div className="flex gap-2 pt-1">
                            <button onClick={() => setTestOpen(false)} disabled={busy}
                                    className="flex-1 h-11 rounded-full border hairline text-white/80 text-sm">
                                Cancel
                            </button>
                            <button onClick={sendTest} disabled={busy || !testTo || form.channel !== "email"}
                                    data-testid="mkt-test-send-confirm"
                                    className="flex-1 h-11 rounded-full bg-gold text-navy font-semibold text-sm disabled:opacity-40">
                                {busy ? "Sending…" : "Send test"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {scheduleOpen && (
                <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center"
                     onClick={() => !busy && setScheduleOpen(false)}
                     data-testid="mkt-schedule-dialog">
                    <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md m-4 rounded-2xl glass glass-strong border hairline p-6 space-y-3">
                        <div className="flex items-center gap-2 text-gold">
                            <Clock className="h-5 w-5" />
                            <h3 className="font-serif text-white text-xl">Schedule this campaign</h3>
                        </div>
                        <p className="text-sm text-white/70">
                            Pick a date and time. The campaign will send automatically the moment it's due.
                        </p>
                        <Field label="Send at">
                            <input type="datetime-local" value={scheduleAt}
                                   onChange={(e) => setScheduleAt(e.target.value)}
                                   data-testid="mkt-schedule-at"
                                   className="w-full h-11 bg-transparent border hairline rounded-md px-3 text-white text-sm" />
                        </Field>
                        <div className="flex gap-2 pt-1">
                            <button onClick={() => setScheduleOpen(false)} disabled={busy}
                                    className="flex-1 h-11 rounded-full border hairline text-white/80 text-sm">
                                Cancel
                            </button>
                            <button onClick={scheduleSend} disabled={busy || !scheduleAt}
                                    data-testid="mkt-schedule-confirm"
                                    className="flex-1 h-11 rounded-full bg-gold text-navy font-semibold text-sm disabled:opacity-40">
                                {busy ? "Scheduling…" : "Schedule"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {confirmSend && preview && (
                <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center"
                     onClick={() => !busy && setConfirmSend(false)}
                     data-testid="mkt-confirm-send">
                    <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md m-4 rounded-2xl glass glass-strong border hairline p-6 space-y-3">
                        <div className="flex items-center gap-2 text-gold">
                            <ShieldCheck className="h-5 w-5" />
                            <h3 className="font-serif text-white text-xl">Send campaign?</h3>
                        </div>
                        <p className="text-sm text-white/75">
                            About to send <span className="text-gold font-semibold">{preview.deliverable ?? preview.eligible}</span> message{(preview.deliverable ?? preview.eligible) === 1 ? "" : "s"}.
                            {(preview.missing_address ?? 0) > 0 && <> {preview.missing_address} consent-eligible customer{preview.missing_address === 1 ? " has" : "s have"} no address on file and will be skipped.</>}
                            {(preview.excluded_opt_out ?? 0) > 0 && <> {preview.excluded_opt_out} opted-out customer{preview.excluded_opt_out === 1 ? " is" : "s are"} excluded.</>}
                        </p>
                        <p className="text-xs text-white/50">Delivery runs in the background at ~5 messages/sec. You can close this dialog. Progress shows on the campaign list.</p>
                        <div className="flex gap-2 pt-2">
                            <button onClick={() => setConfirmSend(false)} disabled={busy}
                                    className="flex-1 h-11 rounded-full border hairline text-white/80 text-sm">
                                Cancel
                            </button>
                            <button onClick={doSend} disabled={busy} data-testid="mkt-confirm-send-btn"
                                    className="flex-1 h-11 rounded-full bg-gold text-navy font-semibold text-sm disabled:opacity-40">
                                {busy ? "Sending…" : "Send now"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Step({ number, title, children }) {
    return (
        <section className="space-y-3">
            <div className="flex items-center gap-2">
                <span className="h-6 w-6 rounded-full bg-gold text-navy text-[11px] font-bold flex items-center justify-center">{number}</span>
                <h3 className="text-sm text-white/90 font-semibold">{title}</h3>
            </div>
            {children}
        </section>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/55 mb-1.5 font-semibold">{label}</div>
            {children}
        </label>
    );
}

/* Live email preview — renders subject + body inside an inbox-style card.
   Inline colours override the .app-light .glass-strong theme remap that
   otherwise flips text-navy to white on the white preview background. */
function EmailPreview({ subject, body }) {
    const rendered = renderWithSamples(body);
    const looksLikeHtml = /<\w+[^>]*>/.test(rendered);
    const html = looksLikeHtml
        ? rendered
        : rendered
            .split(/\n\s*\n/)
            .map((p) => `<p>${p.replace(/\n/g, "<br/>")}</p>`)
            .join("");
    return (
        <div className="rounded-xl shadow-lg overflow-hidden" style={{ background: "#ffffff" }} data-testid="mkt-email-preview">
            <div className="px-4 py-3" style={{ background: "#f5f6fa", borderBottom: "1px solid #e5e7ef" }}>
                <div className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color: "#6b7391" }}>From</div>
                <div className="text-sm font-medium" style={{ color: "#0F1A3D" }}>Pout Aesthetics &lt;hello@poutaesthetics.co.uk&gt;</div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-semibold mt-2" style={{ color: "#6b7391" }}>Subject</div>
                <div className="text-base font-serif" style={{ color: "#0F1A3D" }} data-testid="mkt-preview-subject">
                    {renderWithSamples(subject) || <span className="italic" style={{ color: "#94a0b6" }}>(No subject)</span>}
                </div>
            </div>
            <div className="px-4 py-4 text-sm leading-relaxed [&_a]:text-gold [&_a]:underline [&_p]:mb-2 [&_p:last-child]:mb-0" style={{ color: "#0F1A3D" }} data-testid="mkt-preview-body">
                {rendered
                    ? <div dangerouslySetInnerHTML={{ __html: html }} />
                    : <p className="italic" style={{ color: "#94a0b6" }}>Start typing your message to see it appear here.</p>}
            </div>
        </div>
    );
}

/* Live SMS/WhatsApp preview — chat-bubble style. */
function SmsPreview({ body, channel }) {
    const rendered = renderWithSamples(body);
    return (
        <div className="rounded-xl shadow-lg p-4" style={{ background: "#ffffff" }} data-testid="mkt-sms-preview">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-semibold mb-3" style={{ color: "#6b7391" }}>
                <MessageSquare className="h-3 w-3" /> {channel === "whatsapp" ? "WhatsApp preview" : "SMS preview"}
            </div>
            <div className="flex justify-end">
                <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${channel === "whatsapp" ? "" : ""}`}
                     style={channel === "whatsapp"
                        ? { background: "#25d366", color: "#ffffff" }
                        : { background: "#eef0f7", color: "#0F1A3D" }}>
                    {rendered || <span className="italic" style={{ color: channel === "whatsapp" ? "rgba(255,255,255,0.75)" : "#94a0b6" }}>Start typing your message to see it appear here.</span>}
                </div>
            </div>
            {rendered && (
                <div className="mt-2 text-[10px] text-right" style={{ color: "#94a0b6" }}>
                    {rendered.length} character{rendered.length === 1 ? "" : "s"}
                </div>
            )}
        </div>
    );
}

/* Advanced numeric filters — hidden by default. */
function AdvancedFilters({ filters, onChange, disabled }) {
    const setF = (k, v) => onChange({ ...filters, [k]: v });
    return (
        <div className="rounded-xl surface-highlight p-3 space-y-3" data-testid="mkt-advanced-filters">
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/70 font-semibold">Fine-tune</div>
            <div className="grid grid-cols-2 gap-3">
                <label className="text-xs text-white/75">
                    <div>Last visit within (days)</div>
                    <input type="number" min="0" disabled={disabled} value={filters.last_appointment_within_days || ""}
                           onChange={(e) => setF("last_appointment_within_days", e.target.value ? parseInt(e.target.value) : null)}
                           data-testid="mkt-filter-days"
                           className="mt-1 w-full h-9 bg-transparent border hairline rounded-md px-2 text-white text-sm" />
                </label>
                <label className="text-xs text-white/75">
                    <div>Birthday month (1-12)</div>
                    <input type="number" min="1" max="12" disabled={disabled} value={filters.birthday_month || ""}
                           onChange={(e) => setF("birthday_month", e.target.value ? parseInt(e.target.value) : null)}
                           data-testid="mkt-filter-bday"
                           className="mt-1 w-full h-9 bg-transparent border hairline rounded-md px-2 text-white text-sm" />
                </label>
                <label className="text-xs text-white/75">
                    <div>Minimum visits</div>
                    <input type="number" min="0" disabled={disabled} value={filters.min_visits || ""}
                           onChange={(e) => setF("min_visits", e.target.value ? parseInt(e.target.value) : null)}
                           data-testid="mkt-filter-visits"
                           className="mt-1 w-full h-9 bg-transparent border hairline rounded-md px-2 text-white text-sm" />
                </label>
                <label className="text-xs text-white/75">
                    <div>Minimum spend (£)</div>
                    <input type="number" min="0" step="0.01" disabled={disabled} value={filters.min_spend || ""}
                           onChange={(e) => setF("min_spend", e.target.value ? parseFloat(e.target.value) : null)}
                           data-testid="mkt-filter-spend"
                           className="mt-1 w-full h-9 bg-transparent border hairline rounded-md px-2 text-white text-sm" />
                </label>
            </div>
        </div>
    );
}

/* ---------------------------------------------------------------------------
 * Templates tab
 * ------------------------------------------------------------------------- */
function TemplatesTab() {
    const [list, setList] = useState(null);
    const load = () => staffApi.listMarketingTemplates().then((r) => setList(r.templates || [])).catch(() => setList([]));
    useEffect(() => { load(); }, []);
    if (!list) return <Skeleton className="h-40" />;
    return (
        <div className="space-y-3">
            <div className="text-sm text-white/60">{list.length} template{list.length === 1 ? "" : "s"} available</div>
            <div className="grid gap-3 sm:grid-cols-2">
                {list.map((t) => (
                    <div key={t.id}
                         data-testid={`mkt-tpl-${t.id}`}
                         className="rounded-xl surface-highlight shadow-md p-4">
                        <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-gold" />
                            <div className="text-white font-semibold text-[15px] flex-1">{t.name}</div>
                            {t.is_system && (
                                <span className="text-[9px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border border-gold/40 text-gold/90">
                                    System
                                </span>
                            )}
                        </div>
                        {t.subject && <div className="mt-2 text-xs text-white/70 line-clamp-2">{t.subject}</div>}
                        <div className="mt-2 text-xs text-white/55 line-clamp-2">{t.body_text || t.sms_body}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default StaffMarketing;
